<template>
    <div class="ticket-tier-manager p-3">
        <div class="d-flex align-items-center mb-3">
            <b-button variant="link" class="p-0 mr-3" @click="$router.go(-1)">
                <i class="fa fa-arrow-left"></i>
            </b-button>
            <h4 class="mb-0">Ticket Tiers</h4>
            <b-button variant="primary" size="sm" class="ml-auto" @click="addTier">
                <i class="fa fa-plus mr-1"></i> Add Tier
            </b-button>
        </div>

        <div v-if="loading" class="text-center py-5">
            <b-spinner variant="primary" />
        </div>

        <div v-else>
            <div v-if="tiers.length === 0" class="text-center text-muted py-5">
                <i class="fa fa-ticket" style="font-size: 2rem;"></i>
                <p class="mt-2">No ticket tiers yet. Add one to get started.</p>
            </div>

            <draggable v-model="tiers" handle=".drag-handle" @end="onSortEnd">
                <div v-for="(tier, index) in tiers" :key="tier.id || tier._tempId" class="tier-card mb-3">
                    <div class="d-flex align-items-start">
                        <div class="drag-handle mr-3 mt-1">
                            <i class="fa fa-bars text-muted"></i>
                        </div>
                        <div class="flex-grow-1">
                            <div class="d-flex align-items-center mb-1">
                                <strong class="mr-2">{{ tier.name }}</strong>
                                <b-badge :variant="typeBadgeVariant(tier.type)" class="mr-2">
                                    {{ tier.type === 'rsvp_only' ? 'RSVP Only' : tier.type }}
                                </b-badge>
                                <b-badge v-if="!tier.is_active" variant="secondary">Inactive</b-badge>
                            </div>
                            <div class="text-muted small">
                                <span v-if="tier.type === 'paid'" class="mr-3">
                                    <strong>{{ tier.currency || 'USD' }} {{ parseFloat(tier.price || 0).toFixed(2) }}</strong>
                                </span>
                                <span v-else class="mr-3 text-success">Free</span>
                                <span v-if="tier.max_quantity" class="mr-3">
                                    Capacity: {{ tier.max_quantity }}
                                    <span v-if="tier.sold_count !== undefined"> ({{ tier.sold_count }} sold)</span>
                                </span>
                                <span v-else class="mr-3">Unlimited</span>
                            </div>
                            <div v-if="tier.description" class="text-muted small mt-1">{{ tier.description }}</div>
                        </div>
                        <div class="d-flex align-items-center">
                            <b-form-checkbox
                                v-model="tier.is_active"
                                switch
                                class="mr-2"
                                @change="markDirty"
                            />
                            <b-button variant="link" size="sm" class="text-primary p-1" @click="editTier(index)">
                                <i class="fa fa-pencil"></i>
                            </b-button>
                            <b-button variant="link" size="sm" class="text-danger p-1" @click="removeTier(index)">
                                <i class="fa fa-trash"></i>
                            </b-button>
                        </div>
                    </div>
                </div>
            </draggable>

            <div v-if="isDirty" class="mt-3">
                <b-button variant="primary" :disabled="saving" @click="saveTiers">
                    <b-spinner v-if="saving" small class="mr-1" />
                    Save Changes
                </b-button>
                <b-button variant="outline-secondary" class="ml-2" @click="loadTiers">Cancel</b-button>
            </div>
        </div>

        <!-- Edit Modal -->
        <b-modal v-model="showEditModal" :title="editIndex === null ? 'Add Ticket Tier' : 'Edit Ticket Tier'" @ok="saveEditModal" ok-title="Save">
            <b-form-group label="Name *">
                <b-form-input v-model="editForm.name" placeholder="e.g. General Admission, VIP" />
            </b-form-group>

            <b-form-group label="Type *">
                <b-form-select v-model="editForm.type" :options="typeOptions" />
            </b-form-group>

            <b-form-group v-if="editForm.type === 'paid'" label="Price *">
                <b-input-group :prepend="editForm.currency || 'USD'">
                    <b-form-input v-model="editForm.price" type="number" step="0.01" min="0" />
                </b-input-group>
            </b-form-group>

            <b-form-group v-if="editForm.type === 'paid'" label="Currency">
                <b-form-input v-model="editForm.currency" placeholder="USD" maxlength="3" />
            </b-form-group>

            <b-form-group label="Max Quantity">
                <b-form-input v-model="editForm.max_quantity" type="number" min="1" placeholder="Leave empty for unlimited" />
            </b-form-group>

            <b-form-group label="Description">
                <b-form-textarea v-model="editForm.description" rows="2" placeholder="Brief description shown to registrants" />
            </b-form-group>

            <b-form-group label="Sale Start Date">
                <b-form-input v-model="editForm.sale_start_date" type="datetime-local" />
            </b-form-group>

            <b-form-group label="Sale End Date">
                <b-form-input v-model="editForm.sale_end_date" type="datetime-local" />
            </b-form-group>

            <b-form-checkbox v-model="editForm.is_active" switch>Active</b-form-checkbox>
        </b-modal>
    </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
    name: 'TicketTierManager',
    components: { draggable },
    data() {
        return {
            loading: false,
            saving: false,
            isDirty: false,
            tiers: [],
            showEditModal: false,
            editIndex: null,
            editForm: this.defaultEditForm(),
            typeOptions: [
                { value: 'free', text: 'Free' },
                { value: 'paid', text: 'Paid' },
                { value: 'rsvp_only', text: 'RSVP Only (Invitation Only)' },
            ],
        }
    },
    computed: {
        workshopId() {
            return this.$route.params.workshopId
        },
    },
    mounted() {
        this.loadTiers()
    },
    methods: {
        defaultEditForm() {
            return {
                name: '',
                type: 'free',
                price: '0.00',
                currency: 'USD',
                max_quantity: null,
                description: '',
                sort_order: 0,
                is_active: true,
                sale_start_date: null,
                sale_end_date: null,
            }
        },
        typeBadgeVariant(type) {
            const map = { free: 'success', paid: 'primary', rsvp_only: 'warning' }
            return map[type] || 'secondary'
        },
        async loadTiers() {
            this.loading = true
            try {
                const data = await this.$store.dispatch('eventInvitation/fetchTicketTiers', this.workshopId)
                this.tiers = data.map(t => ({ ...t }))
                this.isDirty = false
            } catch (err) {
                this.$bvToast.toast('Failed to load ticket tiers.', { variant: 'danger', solid: true })
            } finally {
                this.loading = false
            }
        },
        markDirty() {
            this.isDirty = true
        },
        addTier() {
            this.editIndex = null
            this.editForm = { ...this.defaultEditForm(), sort_order: this.tiers.length }
            this.showEditModal = true
        },
        editTier(index) {
            this.editIndex = index
            const tier = this.tiers[index]
            this.editForm = {
                name: tier.name,
                type: tier.type,
                price: tier.price || '0.00',
                currency: tier.currency || 'USD',
                max_quantity: tier.max_quantity,
                description: tier.description || '',
                sort_order: tier.sort_order ?? index,
                is_active: tier.is_active !== false,
                sale_start_date: tier.sale_start_date ? tier.sale_start_date.slice(0, 16) : null,
                sale_end_date: tier.sale_end_date ? tier.sale_end_date.slice(0, 16) : null,
            }
            this.showEditModal = true
        },
        saveEditModal(evt) {
            evt.preventDefault()
            if (!this.editForm.name) {
                this.$bvToast.toast('Name is required.', { variant: 'danger', solid: true })
                return
            }
            const tierData = { ...this.editForm }
            if (tierData.type !== 'paid') {
                tierData.price = '0.00'
            }
            tierData.max_quantity = tierData.max_quantity ? parseInt(tierData.max_quantity) : null

            if (this.editIndex !== null) {
                const existing = this.tiers[this.editIndex]
                this.$set(this.tiers, this.editIndex, { ...existing, ...tierData })
            } else {
                tierData._tempId = Date.now()
                this.tiers.push(tierData)
            }

            this.isDirty = true
            this.showEditModal = false
        },
        removeTier(index) {
            this.tiers.splice(index, 1)
            this.isDirty = true
        },
        onSortEnd() {
            this.tiers.forEach((t, i) => { t.sort_order = i })
            this.isDirty = true
        },
        async saveTiers() {
            this.saving = true
            try {
                const payload = this.tiers.map((t, i) => ({
                    id: t.id || null,
                    name: t.name,
                    type: t.type,
                    price: t.price,
                    currency: t.currency || 'USD',
                    max_quantity: t.max_quantity,
                    description: t.description,
                    sort_order: i,
                    is_active: t.is_active !== false,
                    sale_start_date: t.sale_start_date || null,
                    sale_end_date: t.sale_end_date || null,
                }))
                const data = await this.$store.dispatch('eventInvitation/updateTicketTiers', {
                    workshopId: this.workshopId,
                    tiers: payload,
                })
                this.tiers = data.map(t => ({ ...t }))
                this.isDirty = false
                this.$bvToast.toast('Ticket tiers saved.', { variant: 'success', solid: true })
            } catch (err) {
                this.$bvToast.toast('Failed to save ticket tiers.', { variant: 'danger', solid: true })
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped>
.tier-card {
    background: white;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 16px;
}
.drag-handle {
    cursor: grab;
}
</style>
